import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { RequestService } from './request.service';

export interface CustomInstruction {
  _id: string;
  name: string;
  content: string;
  created_by: string;
  parent_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomInstructionRequest {
  name: string;
  content: string;
  parent_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CustomInstructionsService {
  private readonly baseUrl = '/custom-instructions';

  // Reactive collection for shared state
  private _customInstructions$ = new BehaviorSubject<CustomInstruction[]>([]);

  constructor(private client: RequestService) {}

  // Return the reactive BehaviorSubject that gets updated by CRUD operations
  getCustomInstructions(): Observable<CustomInstruction[]> {
    // If we haven't loaded data yet, trigger initial load
    if (this._customInstructions$.value.length === 0) {
      // Trigger initial load and update the BehaviorSubject
      this.client
        .get<CustomInstruction[]>({
          uri: this.baseUrl,
          handlers: {
            success: () => void 0,
            error: () => void 0,
          },
        })
        .subscribe({
          next: instructions => {
            this._customInstructions$.next(instructions);
          },
          error: error => {
            console.error('Error loading custom instructions:', error);
            // Keep the empty array in case of error
          },
        });
    }

    // Return the reactive BehaviorSubject that gets updated by CRUD operations
    return this._customInstructions$.asObservable();
  }

  createCustomInstruction(
    data: CreateCustomInstructionRequest,
  ): Observable<CustomInstruction> {
    return this.client
      .post<CreateCustomInstructionRequest, CustomInstruction>({
        uri: this.baseUrl,
        body: data,
        handlers: {
          success: () => void 0,
          error: () => void 0,
        },
      })
      .pipe(
        tap(newInstruction => {
          // Update the shared collection after server confirmation
          const currentInstructions = this._customInstructions$.value;
          this._customInstructions$.next([
            newInstruction,
            ...currentInstructions,
          ]);
          // No need to trigger reload - existing subscribers get the update via BehaviorSubject
        }),
      );
  }

  updateCustomInstruction(
    id: string,
    data: Partial<CreateCustomInstructionRequest>,
  ): Observable<CustomInstruction> {
    return this.client
      .put<Partial<CreateCustomInstructionRequest>, CustomInstruction>({
        uri: `${this.baseUrl}/${id}`,
        body: data,
        handlers: {
          success: () => void 0,
          error: () => void 0,
        },
      })
      .pipe(
        tap(updatedInstruction => {
          // Update the shared collection after server confirmation
          const currentInstructions = this._customInstructions$.value;
          const updatedInstructions = currentInstructions.map(instruction =>
            instruction._id === updatedInstruction._id
              ? updatedInstruction
              : instruction,
          );
          this._customInstructions$.next(updatedInstructions);
          // No need to trigger reload - existing subscribers get the update via BehaviorSubject
        }),
      );
  }

  deleteCustomInstruction(id: string): Observable<void> {
    return this.client
      .delete<void>({
        uri: `${this.baseUrl}/${id}`,
        handlers: {
          success: () => void 0,
          error: () => void 0,
        },
      })
      .pipe(
        tap(() => {
          // Update the shared collection after server confirmation
          const currentInstructions = this._customInstructions$.value;
          const filteredInstructions = currentInstructions.filter(
            instruction => instruction._id !== id,
          );
          this._customInstructions$.next(filteredInstructions);
          // No need to trigger reload - existing subscribers get the update via BehaviorSubject
        }),
      );
  }

  checkNameAvailability(
    name: string,
  ): Observable<{ available: boolean; name: string }> {
    return this.client.get<{ available: boolean; name: string }>({
      uri: `${this.baseUrl}/check-name/${encodeURIComponent(name)}`,
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }

  getRootInstructions(): Observable<CustomInstruction[]> {
    return this.client.get<CustomInstruction[]>({
      uri: `${this.baseUrl}/roots`,
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }

  getInstructionWithContext(id: string): Observable<{
    instruction: CustomInstruction;
    ancestry: CustomInstruction[];
    children: CustomInstruction[];
    isRoot: boolean;
    hasChildren: boolean;
  }> {
    return this.client.get<{
      instruction: CustomInstruction;
      ancestry: CustomInstruction[];
      children: CustomInstruction[];
      isRoot: boolean;
      hasChildren: boolean;
    }>({
      uri: `${this.baseUrl}/${id}/context`,
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }

  createBranchedInstruction(
    parentId: string,
    data: CreateCustomInstructionRequest,
  ): Observable<CustomInstruction> {
    return this.client
      .post<CreateCustomInstructionRequest, CustomInstruction>({
        uri: `${this.baseUrl}/${parentId}/branch`,
        body: data,
        handlers: {
          success: () => void 0,
          error: () => void 0,
        },
      })
      .pipe(
        tap(newInstruction => {
          // Update the shared collection after server confirmation
          const currentInstructions = this._customInstructions$.value;
          this._customInstructions$.next([
            newInstruction,
            ...currentInstructions,
          ]);
          // No need to trigger reload - existing subscribers get the update via BehaviorSubject
        }),
      );
  }

  getTreeStructure(): Observable<CustomInstruction[]> {
    return this.client.get<CustomInstruction[]>({
      uri: `${this.baseUrl}/tree`,
      handlers: {
        success: () => void 0,
        error: () => void 0,
      },
    });
  }
}
